<template>
  <v-card tile>
    <v-card-title>{{ isNew ? "Nueva plantilla" : "Editar plantilla" }}</v-card-title>
    <v-card-text>
      <v-text-field
        v-model="form.name"
        label="Nombre"
        :disabled="saving"
      />

      <!-- El asunto vive en el Envio, no en la plantilla. -->
      <div class="body-2 grey--text mt-2 mb-2">
        Marcadores disponibles: {{ availableTokens.join(", ") }}.
        $DIA_HABIL_N es el primer día hábil desde el día N del mes del envío
        (por ejemplo $DIA_HABIL_20: si el 20 cae domingo, pone 21).
      </div>

      <v-select
        v-model="form.purpose"
        :items="purposeOptions"
        item-text="text"
        item-value="value"
        label="Propósito"
        :disabled="saving"
      />

      <v-select
        v-model="form.visualStyle"
        :items="visualStyleOptions"
        item-text="text"
        item-value="value"
        label="Estilo visual"
        :disabled="saving"
      />

      <div class="body-2 mt-4 mb-1">Encabezado</div>
      <vue-editor v-model="form.header" :editor-toolbar="editorToolbar" />

      <div class="body-2 mt-4 mb-1">Texto</div>
      <vue-editor v-model="form.bodyText" :editor-toolbar="editorToolbar" />

      <div class="body-2 mt-4 mb-1">Pie</div>
      <vue-editor v-model="form.footerText" :editor-toolbar="editorToolbar" />

      <v-switch
        v-if="showDueDatesToggle"
        v-model="form.showDueDates"
        label="Mostrar fecha de vencimiento"
        :disabled="saving"
      />

      <v-switch
        v-if="showInformNextMonthToggle"
        v-model="form.informNextMonth"
        label="Informar obligaciones que vencen el mes entrante"
        :disabled="saving"
      />

      <v-select
        v-model="form.signature"
        :items="signatures"
        item-text="name"
        item-value="_id"
        label="Firma"
        clearable
        :loading="loadingSignatures"
        :disabled="saving || loadingSignatures"
      />

      <!-- Sin cuenta, el correo sale con la direccion de la empresa y el nombre
           fijo del sistema, como siempre. -->
      <v-select
        v-model="form.account"
        :items="accounts"
        item-text="label"
        item-value="_id"
        label="Cuenta remitente"
        hint="Vacío = la dirección de la empresa, con el nombre del sistema"
        persistent-hint
        clearable
        :loading="loadingAccounts"
        :disabled="saving || loadingAccounts"
      />

      <div class="body-1 mt-4 mb-2">Tema</div>

      <v-text-field
        v-model="form.theme.fontFamily"
        label="Familia tipográfica"
        :placeholder="inheritedTheme.fontFamily || 'la del cliente de correo'"
        persistent-placeholder
        hint="Vacío = usar el del estilo visual"
        persistent-hint
        :disabled="saving"
      />

      <v-text-field
        v-model="form.theme.fontSize"
        label="Tamaño de fuente"
        :placeholder="inheritedTheme.fontSize || 'el del cliente de correo'"
        persistent-placeholder
        hint="Vacío = usar el del estilo visual"
        persistent-hint
        :disabled="saving"
      />

      <v-text-field
        v-model="form.theme.dueDateColor"
        label="Color del vencimiento"
        :placeholder="inheritedTheme.dueDateColor || 'el del estilo visual'"
        persistent-placeholder
        hint="Vacío = usar el del estilo visual"
        persistent-hint
        :disabled="saving"
      />

      <template v-if="showTableToggles">
        <v-select
          v-model="form.theme.tableBorders"
          :items="TRISTATE"
          item-text="text"
          item-value="value"
          label="Bordes de tabla"
          :disabled="saving"
        />

        <v-select
          v-model="form.theme.tableStriped"
          :items="TRISTATE"
          item-text="text"
          item-value="value"
          label="Filas rayadas"
          :disabled="saving"
        />
      </template>
      <!-- El estilo simple no renderiza ninguna tabla -es una lista con
           guiones-, y las reglas de bordes y rayado del tema apuntan a
           <table>. Ofrecer estos interruptores ahi prometeria algo que el
           correo no cumple. -->
      <div v-else class="body-2 grey--text mb-2">
        El estilo simple no usa tablas: los bordes y el rayado no aplican.
      </div>

      <v-switch v-model="form.isActive" label="Activa" :disabled="saving" />

      <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
    </v-card-text>
    <v-card-actions>
      <v-btn :loading="previewing" :disabled="previewing || saving || !form.purpose || !form.visualStyle" @click="openPreview">
        Vista previa
      </v-btn>
      <v-spacer />
      <v-btn :disabled="saving" @click="cancel">Cancelar</v-btn>
      <v-btn
        class="primary"
        :loading="saving"
        :disabled="!form.name || !form.purpose || !form.visualStyle || saving"
        @click="save"
      >Guardar</v-btn>
    </v-card-actions>

    <v-dialog v-if="preview" :value="true" max-width="900" @input="preview = null">
      <v-card>
        <v-card-title>Vista previa — {{ form.name || "sin nombre" }}</v-card-title>
        <v-card-text>
          <!-- iframe y no v-html: el HTML del correo trae su propio <style>, y
               inyectarlo en la pagina le pisaria los estilos a la aplicacion.
               sandbox="" (sin ninguna excepcion) porque un srcdoc hereda el
               origen del padre: el HTML lo escribe un SYS_ADMIN, asi que no es
               una vulnerabilidad, pero un correo no necesita ni scripts ni
               formularios ni navegacion y cerrar el tema cuesta una palabra.
               Las imagenes data: de los iconos del estilo 3 siguen cargando:
               no dependen del origen. -->
          <iframe
            :srcdoc="preview.html"
            sandbox=""
            style="width:100%;height:60vh;border:1px solid #ddd"
          ></iframe>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="preview = null">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
"use strict";

import { VueEditor } from "vue2-editor";
import emailTemplateService from "@/services/EmailTemplateService";
import emailSignatureService from "@/services/EmailSignatureService";
import emailAccountService from "@/services/EmailAccountService";

// Los dos booleanos del tema van como tres opciones -heredar, si, no- y no
// como v-checkbox: un checkbox no puede expresar "heredar del estilo", y con
// false como valor por defecto nadie podria volver al default.
const TRISTATE = [
  { value: null, text: "Heredar del estilo" },
  { value: true, text: "Sí" },
  { value: false, text: "No" }
];

// Los mismos dos propositos que usan showDueDates/informNextMonth en
// template-context.js, duplicados aca como en edit-email-delivery.view.vue:
// el frontend no importa el modulo de constantes del backend.
const PURPOSE_DAILY = "daily";
const PURPOSE_MONTHLY = "monthly";
// Los dos propositos que van a direcciones sueltas y no a una contratista.
const PURPOSE_TAG = "tag";
const PURPOSE_MONTH_END_REPORT = "monthEndReport";

// Los cuatro marcadores que dependen de una contratista concreta. En etiquetas
// y fin de mes no hay una sola -el correo va a direcciones sueltas-, asi que el
// envio real los deja vacios (ver companyTokenValues en
// domains/deliveries/delivery-render.js). Ofrecerlos igual haria que el
// administrador los escriba y despues le lleguen borrados al destinatario.
// $PLURAL_CONTACTOS es la "s" que concuerda con $CONTACTO cuando enumera mas
// de un nombre ("Estimado$PLURAL_CONTACTOS $CONTACTO").
const CONTRACTOR_TOKENS = ["$NOMBRE", "$APELLIDO", "$CONTACTO", "$PLURAL_CONTACTOS", "$CONTRATISTA"];
// $HOY es la fecha de la corrida; $FECHA, la fecha limite que se le pide a la
// contratista. Sirven en el asunto y en el texto por igual.
const SHARED_TOKENS = ["$EMPRESA", "$NOMBRE_CONTACTO", "$APELLIDO_CONTACTO", "$CORREO", "$FECHA", "$HOY", "$MES", "$MES_NUMERO", "$MES_ANTERIOR", "$AÑO", "$DIA_HABIL_N"];

export default {
  name: "edit-email-template",
  components: { VueEditor },
  props: {
    template: { type: Object, required: true },
    purposeOptions: { type: Array, required: true },
    visualStyleOptions: { type: Array, required: true }
  },
  data() {
    return {
      // Formulario armado campo por campo -y no con un Object.assign ciego del
      // objeto de origen- porque template.signature puede llegar como el
      // objeto poblado (desde el listado) o como un id crudo (recien
      // guardado), y theme puede faltar alguna clave si nunca se completo:
      // Mongoose no le pone default a ninguna. Todas las claves, incluidas
      // las de theme, quedan declaradas aca desde el arranque: Vue 2 no
      // detecta la creacion de una propiedad de nivel superior que nunca
      // existio en data().
      form: {
        name: this.template.name || "",
        purpose: this.template.purpose || "",
        visualStyle: this.template.visualStyle || null,
        header: this.template.header || "",
        bodyText: this.template.bodyText || "",
        footerText: this.template.footerText || "",
        showDueDates: this.template.showDueDates !== undefined ? this.template.showDueDates : true,
        informNextMonth: this.template.informNextMonth !== undefined ? this.template.informNextMonth : true,
        signature: (this.template.signature && this.template.signature._id) || this.template.signature || "",
        account: (this.template.account && this.template.account._id) || this.template.account || "",
        theme: Object.assign(
          { fontFamily: "", fontSize: "", dueDateColor: "", tableBorders: null, tableStriped: null },
          this.template.theme || {}
        ),
        isActive: this.template.isActive !== undefined ? this.template.isActive : true
      },
      signatures: [],
      loadingSignatures: false,
      accounts: [],
      loadingAccounts: false,
      saving: false,
      previewing: false,
      // { html } que devuelve el backend, o null con la previa cerrada.
      preview: null,
      errorMessage: null,
      TRISTATE,
      editorToolbar: [
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline"],
        [{ color: [] }],
        ["link"],
        [{ align: [] }],
        ["clean"]
      ]
    };
  },
  computed: {
    isNew() {
      return !this.template._id;
    },
    // El tema del estilo elegido, para mostrarlo como placeholder. Se
    // recalcula al cambiar de estilo: eso es lo que hace visible que el tema
    // guarda solo overrides y no una copia.
    inheritedTheme() {
      const style = this.visualStyleOptions.find(s => s.value === this.form.visualStyle);
      return (style && style.theme) || {};
    },
    // El estilo simple (id 2) no dibuja tablas: los interruptores de bordes y
    // rayado no tienen ningun efecto ahi, asi que no se ofrecen.
    showTableToggles() {
      return this.form.visualStyle !== 2;
    },
    // La pantalla ofrece solo los marcadores que ese proposito resuelve de
    // verdad. Un correo por etiquetas o de fin de mes va a direcciones sueltas,
    // no a una contratista, asi que los cuatro que dependen de una se emiten
    // vacios: ofrecerlos seria prometer algo que el correo no cumple.
    availableTokens() {
      const goesToLooseAddresses =
        this.form.purpose === PURPOSE_TAG ||
        this.form.purpose === PURPOSE_MONTH_END_REPORT;
      return goesToLooseAddresses
        ? SHARED_TOKENS
        : CONTRACTOR_TOKENS.concat(SHARED_TOKENS);
    },
    // Decision de contenido, no override de estilo: solo tiene sentido en los
    // propositos que muestran una fecha de vencimiento por obligacion.
    showDueDatesToggle() {
      return this.form.purpose === PURPOSE_DAILY || this.form.purpose === PURPOSE_MONTHLY;
    },
    // informNextMonth solo lo lee monthly-selection.js para el proposito
    // mensual (ver domains/deliveries/monthly-selection.js).
    showInformNextMonthToggle() {
      return this.form.purpose === PURPOSE_MONTHLY;
    }
  },
  async mounted() {
    await Promise.all([this.loadSignatures(), this.loadAccounts()]);
  },
  methods: {
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async loadAccounts() {
      this.loadingAccounts = true;
      try {
        const accounts = await emailAccountService.list();
        this.accounts = accounts.map(a => Object.assign({ label: `${a.name} <${a.address}>` }, a));
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar las cuentas de correo");
      } finally {
        this.loadingAccounts = false;
      }
    },
    async loadSignatures() {
      this.errorMessage = null;
      this.loadingSignatures = true;
      try {
        this.signatures = await emailSignatureService.list();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar las firmas");
      } finally {
        this.loadingSignatures = false;
      }
    },
    // El backend guarda overrides, no una copia del tema: vacio significa
    // "usar el valor del estilo visual".
    //
    // Los campos que el usuario vacio viajan como `null` EXPLICITO, y NO se
    // omiten del payload. El motivo es concreto y no hay que "simplificarlo":
    // en email-template.model.js `theme` esta declarado como nested path -un
    // objeto literal con hojas tipadas- y no como mongoose.Schema, y
    // mongoose 5.10.2 aplana un `$set: { theme: {...} }` sobre un nested path
    // a claves punteadas (`theme.fontSize`). Las claves que el $set no
    // menciona SOBREVIVEN intactas en el documento, asi que omitir la clave
    // vaciada no la borra: la pantalla mostraria el campo vacio -con el
    // placeholder heredado- mientras el correo sigue renderizando el override
    // viejo, y "Heredar del estilo" de los dos tri-estado seria un camino
    // muerto.
    //
    // `null` significa "borrar", el mismo criterio que el logo de las firmas.
    // Del lado del renderer no hace falta nada mas: catalog.resolveTheme ya
    // trata null, undefined y la cadena vacia por igual ("usar el del
    // estilo").
    //
    // `false` NO es vacio: apagar los bordes o el rayado es un override
    // valido y tiene que viajar como `false`.
    buildPayload() {
      const theme = {};
      Object.keys(this.form.theme).forEach(key => {
        const value = this.form.theme[key];
        const isEmpty = value === null || value === undefined || value === "";
        theme[key] = isEmpty ? null : value;
      });
      return Object.assign({}, this.form, { theme: theme });
    },
    cancel() {
      this.$emit("cancel");
    },
    async save() {
      if (!this.form.name || !this.form.purpose || !this.form.visualStyle || this.saving) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        const saved = this.isNew
          ? await emailTemplateService.create(payload)
          : await emailTemplateService.edit(this.template._id, payload);
        this.$emit("saved", saved);
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo guardar la plantilla");
      } finally {
        this.saving = false;
      }
    },
    // El backend valida los tres campos de texto del tema tambien aca, no
    // solo al guardar: una comilla, un < o un ; en fontFamily/fontSize/
    // dueDateColor vuelve con 400 y un mensaje que nombra el campo. Se
    // muestra tal cual viene, no un texto generico, para que el usuario
    // entienda que campo corregir.
    async openPreview() {
      if (!this.form.purpose || !this.form.visualStyle || this.previewing) return;
      this.previewing = true;
      this.errorMessage = null;
      try {
        this.preview = await emailTemplateService.preview(this.buildPayload());
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo generar la vista previa");
      } finally {
        this.previewing = false;
      }
    }
  }
};
</script>
